import React from 'react'
import PropTypes from 'prop-types'
import { IconButton } from '@material-ui/core'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { useSelector } from 'react-redux'
import { ContextMenuListItem, Popover } from '../../../components/common/Dialogs/Popover'
import ListItemTextBase from '../../../components/common/Containers/Base/ListItemTextBase'
import ConfirmDialog from '../../../components/generics/Confirm/Dialog'
import usePopover from '../../../hooks/usePopover'
import useBooleanHandlers from '../../../hooks/useBooleanHandlers'
import { useTrans } from '../../../services/i18n'
import { useAlertContext } from '../../../contexts/AlertContext'
import { selectUserPermissions, isEnabled } from '../../../selectors'

const MODULE_KEY = 'event-management'

const EventMenu = ({ event, onDelete, onDuplicate, onEdit }) => {
  const { trans } = useTrans()
  const [anchorEl, onOpenPopover, onClosePopover] = usePopover()
  const [openConfirm, onOpenConfirm, onCloseConfirm] = useBooleanHandlers()
  const { successAlert, errorAlert } = useAlertContext()
  const permissions = useSelector(selectUserPermissions)

  const canEdit = isEnabled(permissions, MODULE_KEY, '', 'edit')
  const canCreate = isEnabled(permissions, MODULE_KEY, '', 'create')
  const canDelete = isEnabled(permissions, MODULE_KEY, '', 'delete')

  const handleDuplicate = () => {
    Promise.resolve(onDuplicate({ id: event.id }))
      .then((res) => {
        if (res?.payload) {
          successAlert(`${event.name} has been duplicated as a draft`)
        }
      })
      .catch((err) => errorAlert(err.message))
  }

  const handleDelete = () => {
    Promise.resolve(onDelete({ id: event.id }))
      .then((res) => {
        if (res?.payload) {
          onCloseConfirm()
          successAlert(`${event.name} has been deleted`)
        }
      })
      .catch((err) => errorAlert(err.message))
  }

  if (!canEdit && !canCreate && !canDelete) return null

  return (
    <>
      <IconButton onClick={onOpenPopover}>
        <MoreHorizIcon />
      </IconButton>

      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={onClosePopover}>
        {canEdit && (
          <ContextMenuListItem button onClick={() => onEdit(event)} onClickCB={onClosePopover}>
            <ListItemTextBase>{trans('edit')}</ListItemTextBase>
          </ContextMenuListItem>
        )}
        {canCreate && (
          <ContextMenuListItem button onClick={handleDuplicate} onClickCB={onClosePopover}>
            <ListItemTextBase>{trans('duplicate')}</ListItemTextBase>
          </ContextMenuListItem>
        )}
        {canDelete && (
          <ContextMenuListItem button onClick={onOpenConfirm} onClickCB={onClosePopover}>
            <ListItemTextBase color="secondary">{trans('delete')}</ListItemTextBase>
          </ContextMenuListItem>
        )}
      </Popover>

      <ConfirmDialog
        open={openConfirm}
        onClose={onCloseConfirm}
        onConfirm={handleDelete}
        title={trans('delete-event')}
        text={`${trans('delete-event-confirm')} "${event.name}"?`}
      />
    </>
  )
}

EventMenu.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
}

export default EventMenu
